import axios from 'axios';

const resolveApiBaseUrl = () => {
  const envBaseUrl = (process.env.REACT_APP_API_URL || '').trim();

  const isProduction = process.env.NODE_ENV === 'production';
  if (isProduction && typeof window !== 'undefined' && window.location?.origin) {
    return window.location.origin;
  }

  if (!isProduction) {
    // 开发环境默认走本地后端，避免指向 npm dev server(3000)。
    return 'http://127.0.0.1:9988';
  }

  // 兜底再走环境变量
  if (envBaseUrl) {
    return envBaseUrl;
  }

  return '';
};

const API_BASE_URL = resolveApiBaseUrl();

export interface ApiResponse<T> {
  code: number;
  message: string;
  data: T;
}

export interface PagedResult<T> {
  list: T[];
  total: number;
  page: number;
  page_size: number;
}

export interface AdminLoginResult {
  token: string;
  username: string;
}

export interface AdminUser {
  id: number;
  nickname: string;
  phone: string;
  avatar: string;
  status: number;
  role: number;
  created_at: number;
}

export interface AdminMessage {
  id: number;
  from_user_id: number;
  to_user_id: number;
  from_user: string;
  to_user: string;
  content: string;
  msg_type: number;
  status: number;
  is_revoke: number;
  created_at: number;
}

export interface AdminStats {
  users: number;
  messages: number;
  friends: number;
  active_users: number;
  recent_users: AdminUser[];
  recent_messages: AdminMessage[];
}

export interface AdminFeedback {
  id: number;
  user_id: number;
  nickname: string;
  phone: string;
  feedback_type: 'suggest' | 'bug' | string;
  content: string;
  images: string[];
  status: number;
  source: number;
  platform: string;
  client_version: string;
  device_info: string;
  created_at: number;
}

export interface AdminWechatConfig {
  app_id: string;
  universal_link: string;
  auth_base_url: string;
  has_app_secret: boolean;
}

export interface AdminSMSConfig {
  base_url: string;
  access_key: string;
  secret_key: string;
  sign_name: string;
  template_id: string;
  has_secret_key: boolean;
}

export interface AdminQiniuConfig {
  access_key: string;
  secret_key: string;
  bucket: string;
  domain: string;
  upload_url: string;
  has_secret_key: boolean;
}

export interface AdminApnsConfig {
  team_id: string;
  key_id: string;
  bundle_id: string;
  sandbox: boolean;
  has_key: boolean;
}

export interface AdminAndroidVersionItem {
  id: number;
  version_name: string;
  version_code: number;
  download_url: string;
  force_update: boolean;
  update_notes: string;
  created_at: number;
}

export interface AdminAndroidUploadResult {
  url: string;
  key: string;
  hash: string;
  size: number;
}

export interface AdminQiniuUploadToken {
  token: string;
  domain: string;
  upload_url: string;
  deadline: number;
}

const api = axios.create({
  baseURL: API_BASE_URL,
  timeout: 10000,
  headers: {
    'Content-Type': 'application/json',
  },
});

// 请求拦截器
api.interceptors.request.use(
  (config) => {
    const token = localStorage.getItem('admin_token');
    if (token) {
      config.headers.Authorization = `Bearer ${token}`;
    }
    return config;
  },
  (error) => Promise.reject(error)
);

// 响应拦截器
api.interceptors.response.use(
  (response) => {
    const payload = response.data;
    if (payload?.code === 40100) {
      localStorage.removeItem('admin_token');
      if (window.location.pathname !== '/login') {
        window.location.href = '/login';
      }
      return Promise.reject(new Error(payload.message || '登录已过期'));
    }
    return payload;
  },
  (error) => {
    if (error.response?.status === 401) {
      localStorage.removeItem('admin_token');
      window.location.href = '/login';
    }
    return Promise.reject(error);
  }
);

// 登录
export const login = (username: string, password: string) => {
  return api.post('/api/v1/admin/login', { username, password }) as Promise<ApiResponse<AdminLoginResult>>;
};

// 用户管理
export const getUsers = (page = 1, pageSize = 10) => {
  return api.get('/api/v1/admin/users', { params: { page, pageSize } }) as Promise<ApiResponse<PagedResult<AdminUser>>>;
};

export const getUserDetail = (id: number) => {
  return api.get(`/api/v1/admin/users/${id}`) as Promise<ApiResponse<AdminUser>>;
};

export const updateUser = (id: number, data: Partial<Pick<AdminUser, 'nickname' | 'phone' | 'status'>>) => {
  return api.post(`/api/v1/admin/users/${id}`, data) as Promise<ApiResponse<AdminUser>>;
};

export const deleteUser = (id: number) => {
  return api.post(`/api/v1/admin/users/${id}/delete`) as Promise<ApiResponse<null>>;
};

// 消息管理
export const getMessages = (
  page = 1,
  pageSize = 10,
  params?: { keyword?: string; msgType?: number }
) => {
  return api.get('/api/v1/admin/messages', { params: { page, pageSize, ...params } }) as Promise<ApiResponse<PagedResult<AdminMessage>>>;
};

export const deleteMessage = (id: number) => {
  return api.post(`/api/v1/admin/messages/${id}/delete`) as Promise<ApiResponse<null>>;
};

// 意见反馈管理
export const getFeedbacks = (
  page = 1,
  pageSize = 10,
  params?: { keyword?: string; feedbackType?: string; status?: number }
) => {
  return api.get('/api/v1/admin/feedbacks', { params: { page, pageSize, ...params } }) as Promise<ApiResponse<PagedResult<AdminFeedback>>>;
};

export const deleteFeedback = (id: number) => {
  return api.post(`/api/v1/admin/feedbacks/${id}/delete`) as Promise<ApiResponse<null>>;
};

// 统计数据
export const getStats = () => {
  return api.get('/api/v1/admin/stats') as Promise<ApiResponse<AdminStats>>;
};

// 微信授权登录配置
export const getWechatConfig = () => {
  return api.get('/api/v1/admin/wechat-config') as Promise<ApiResponse<AdminWechatConfig>>;
};

export const updateWechatConfig = (data: {
  app_id: string;
  app_secret?: string;
  universal_link?: string;
  auth_base_url?: string;
}) => {
  return api.post('/api/v1/admin/wechat-config', data) as Promise<ApiResponse<AdminWechatConfig>>;
};

// 短信配置
export const getSMSConfig = () => {
  return api.get('/api/v1/admin/sms-config') as Promise<ApiResponse<AdminSMSConfig>>;
};

export const updateSMSConfig = (data: {
  base_url: string;
  access_key: string;
  secret_key?: string;
  sign_name?: string;
  template_id?: string;
}) => {
  return api.post('/api/v1/admin/sms-config', data) as Promise<ApiResponse<AdminSMSConfig>>;
};

// 七牛配置
export const getQiniuConfig = () => {
  return api.get('/api/v1/admin/qiniu-config') as Promise<ApiResponse<AdminQiniuConfig>>;
};

export const updateQiniuConfig = (data: {
  access_key: string;
  secret_key?: string;
  bucket: string;
  domain: string;
  upload_url?: string;
}) => {
  return api.post('/api/v1/admin/qiniu-config', data) as Promise<ApiResponse<AdminQiniuConfig>>;
};

// APNs 推送配置
export const getApnsConfig = () => {
  return api.get('/api/v1/admin/apns-config') as Promise<ApiResponse<AdminApnsConfig>>;
};

export const updateApnsConfig = (data: {
  team_id: string;
  key_id: string;
  bundle_id: string;
  sandbox: boolean;
  key_content?: string;
}) => {
  return api.post('/api/v1/admin/apns-config', data) as Promise<ApiResponse<AdminApnsConfig>>;
};

// 安卓版本配置
export const getAndroidVersionConfig = () => {
  return api.get('/api/v1/admin/android-versions', { params: { page: 1, pageSize: 100 } }) as Promise<ApiResponse<PagedResult<AdminAndroidVersionItem>>>;
};

export const createAndroidVersion = (data: {
  version_name: string;
  version_code: number;
  download_url: string;
  force_update: boolean;
  update_notes?: string;
}) => {
  return api.post('/api/v1/admin/android-versions', data) as Promise<ApiResponse<AdminAndroidVersionItem>>;
};

export const uploadAndroidVersionPackage = (file: File) => {
  const formData = new FormData();
  formData.append('file', file);
  return api.post('/api/v1/admin/android-versions/upload', formData, {
    headers: {
      'Content-Type': 'multipart/form-data',
    },
  }) as Promise<ApiResponse<AdminAndroidUploadResult>>;
};

export const getQiniuUploadToken = () => {
  return api.get('/api/v1/admin/qiniu-upload-token') as Promise<ApiResponse<AdminQiniuUploadToken>>;
};

// 条款管理
export interface AdminAgreement {
  type: string;
  title: string;
  content: string;
  update_date: string;
}

export const getAgreementTerms = () => {
  return api.get('/api/v1/admin/agreement/terms') as Promise<ApiResponse<AdminAgreement>>;
};

export const updateAgreementTerms = (data: { content: string; update_date?: string }) => {
  return api.post('/api/v1/admin/agreement/terms', data) as Promise<ApiResponse<null>>;
};

export const getAgreementPrivacy = () => {
  return api.get('/api/v1/admin/agreement/privacy') as Promise<ApiResponse<AdminAgreement>>;
};

export const updateAgreementPrivacy = (data: { content: string; update_date?: string }) => {
  return api.post('/api/v1/admin/agreement/privacy', data) as Promise<ApiResponse<null>>;
};

export default api;
